<script setup lang="ts">
const { t, tm, rt } = useI18n() as any;

const activeExample = ref(0);

const scrollToPricing = () => {
  const element = document.getElementById('pricing');
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};

const apiExamples = ref([
  {
    title: t('viberCarousel.api.restful.title'),
    description: t('viberCarousel.api.restful.description'),
    language: 'JSON',
    code: `{
  "messages": [
    {
      "from": "ALPHANAME",
      "to": "380936670003",
      "type": "viber_promo",
      "text": "Text for Viber",
      "ttl": 300,
      "messageData": {
        "carousel": {
          "items": [
            {
              "title": "50% Off on All Shoes!",
              "imageUrl": "https://cdn-web.smsbat.com/help/carusel.png",
              "primaryButton": {
                "label": "Shop Now",
                "actionUrl": "https://help.noetikos.org"
              },
              "secondaryButton": {
                "label": "View Details",
                "actionUrl": "https://omni.noetikos.org"
              }
            },
            {
              "title": "New Arrivals: Summer Collection",
              "imageUrl": "https://cdn-web.smsbat.com/help/carusel.png",
              "primaryButton": {
                "label": "Explore",
                "actionUrl": "https://help.noetikos.org"
              },
              "secondaryButton": {
                "label": "Learn More",
                "actionUrl": "https://omni.noetikos.org"
              }
            }
          ]
        }
      }
    }
  ]
}`
  },
  {
    title: t('viberCarousel.api.cascade.title'),
    description: t('viberCarousel.api.cascade.description'),
    language: 'JSON',
    code: String.raw`curl --location 'http://api.counterbat.com/api/CascadeMessage/send_message/' \\
--header 'X-Viber-Auth-Token: <Viber-Bot-API-Key>' \\
--header 'X-Authorization-Key: <API-Key>' \\
--header 'Content-Type: application/json' \\
--data '[
  {
    "id": "11111112",
    "fromName": "ALPHANAME",
    "toPhone": "380669227498",
    "messageType": "viber_carousel",
    "ttl": 80,
    "viberMessage": {
      "ttl": 60,
      "text": "Hello world!",
      "carousel": {
        "items": [
          {
            "title": "50% Off on All Shoes!",
            "imageUrl": "https://5.imimg.com/data5/SELLER/Default/2023/7/323356025/UR/EQ/WS/192140499/safeimagekit-resized-img-3--500x500.png",
            "primaryButton": {
              "label": "Shop Now",
              "actionUrl": "https://example.com/shoes-sale"
            },
            "secondaryButton": {
              "label": "View Details",
              "actionUrl": "https://example.com/shoes"
            }
          },
          {
            "title": "New Arrivals: Summer Collection",
            "imageUrl": "https://media.istockphoto.com/id/1443562748/photo/cute-ginger-cat.jpg?s=612x612&w=0&k=20&c=vvM97wWz-hMj7DLzfpYRmY2VswTqcFEKkC437hxm3Cg=",
            "primaryButton": {
              "label": "Explore",
              "actionUrl": "https://example.com/summer"
            },
            "secondaryButton": {
              "label": "Learn More",
              "actionUrl": "https://example.com/summer-info"
            }
          }
        ]
      }
    },
    "customerData": {
      "type": "data"
    }
  }
]'`
  }
]);

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
};

// SEO
useHead({
  title: t('viberCarousel.seo.title'),
  meta: [
    {
      name: 'description',
      content: t('viberCarousel.seo.description')
    },
    {
      name: 'keywords',
      content: t('viberCarousel.seo.keywords')
    }
  ]
});
</script>

<template>
  <div class="viber-carousel-page">
    <!-- Hero Section -->
    <div class="main-banner">
      <div class="container">
        <div class="banner-content">
          <div class="banner-carousel-text">
            <h1 class="banner-title">{{ $t('viberCarousel.hero.title') }}</h1>
            <p class="banner-description">
              {{ $t('viberCarousel.hero.subtitle') }}
            </p>
            <p class="banner-description-secondary">
              {{ $t('viberCarousel.hero.description') }}
            </p>
            <div class="banner-action">
              <NuxtLink to="https://omni.smsbat.com/register-ca" external target="_blank"
                class="nav-link button filled whitespace-nowrap">
                {{ $t('viberCarousel.hero.getStarted') }}
              </NuxtLink>
              <NuxtLink @click="scrollToPricing" class="button outlined cursor-pointer" external>{{
                $t('viberCarousel.hero.viewPricing') }}
              </NuxtLink>
            </div>
          </div>
          <div class="banner-image-container">
            <NuxtImg src="/images/viber_carousel-preview.webp" width="800" height="600" format="webp" loading="eager" fetchpriority="high" class="mt-20 w-full h-auto object-contain" alt="Viber Carousel Preview" />
          </div>
        </div>
      </div>
    </div>

    <!-- Global Stats Section -->
    <div class="page-section bg-gray-50">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('viberCarousel.stats.title') }}</div>
          <div class="section-head-description">{{ $t('viberCarousel.stats.subtitle') }}</div>
        </div>
        <div class="section-content">
          <div class="stats-grid">
            <div class="stat-item">
              <div class="stat-number">63%</div>
              <div class="stat-label">{{ $t('viberCarousel.stats.primeActivity') }}</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">47%</div>
              <div class="stat-label">{{ $t('viberCarousel.stats.women') }}</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">53%</div>
              <div class="stat-label">{{ $t('viberCarousel.stats.men') }}</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">78%</div>
              <div class="stat-label">{{ $t('viberCarousel.stats.mobile') }}</div>
            </div>
          </div>
          <div class="top-countries">
            <h3>{{ $t('viberCarousel.stats.topCountries.title') }}</h3>
            <div class="countries-grid">
              <div class="country-group">
                <h4>{{ $t('viberCarousel.stats.topCountries.cee') }}</h4>
                <div class="country-list">
                  <span class="country-item">Albania 80%</span>
                  <span class="country-item">Bosnia & Herzegovina 98%</span>
                  <span class="country-item">Bulgaria 94%</span>
                  <span class="country-item">Croatia 78%</span>
                  <span class="country-item">Georgia 71%</span>
                  <span class="country-item">Greece 91%</span>
                  <span class="country-item">Hungary 68%</span>
                  <span class="country-item">Macedonia 85%</span>
                  <span class="country-item">Montenegro 96%</span>
                  <span class="country-item">Serbia 97%</span>
                  <span class="country-item">Slovakia 40%</span>
                  <span class="country-item">Slovenia 88%</span>
                  <span class="country-item">Ukraine 98%</span>
                </div>
              </div>
              <div class="country-group">
                <h4>{{ $t('viberCarousel.stats.topCountries.cis') }}</h4>
                <div class="country-list">
                  <span class="country-item">Armenia 93%</span>
                  <span class="country-item">Belarus 86%</span>
                  <span class="country-item">Moldova 82%</span>
                </div>
              </div>
              <div class="country-group">
                <h4>{{ $t('viberCarousel.stats.topCountries.sea') }}</h4>
                <div class="country-list">
                  <span class="country-item">Maldives 76%</span>
                  <span class="country-item">Myanmar 67%</span>
                  <span class="country-item">Nepal 36%</span>
                  <span class="country-item">Philippines 71%</span>
                  <span class="country-item">Sri Lanka 24%</span>
                  <span class="country-item">Vietnam 26%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Features Section -->
    <div class="page-section">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('viberCarousel.features.title') }}</div>
          <div class="section-head-description">{{ $t('viberCarousel.features.subtitle') }}</div>
        </div>
        <div class="section-content">
          <div class="features-grid">
            <div class="feature-item">
              <div class="feature-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                  <path d="M2 17L12 22L22 17" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                  <path d="M2 12L12 17L22 12" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              </div>
              <h3>{{ $t('viberCarousel.features.interactive.title') }}</h3>
              <p>{{ $t('viberCarousel.features.interactive.description') }}</p>
            </div>

            <div class="feature-item">
              <div class="feature-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 16L8 12L12 16L20 8" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                  <path
                    d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="#3070b6" stroke-width="2" />
                </svg>
              </div>
              <h3>{{ $t('viberCarousel.features.engagement.title') }}</h3>
              <p>{{ $t('viberCarousel.features.engagement.description') }}</p>
            </div>

            <div class="feature-item">
              <div class="feature-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="#3070b6" stroke-width="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" fill="#3070b6" />
                  <path d="M21 15L16 10L5 21" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              </div>
              <h3>{{ $t('viberCarousel.features.visual.title') }}</h3>
              <p>{{ $t('viberCarousel.features.visual.description') }}</p>
            </div>

            <div class="feature-item">
              <div class="feature-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12L11 14L15 10" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                  <path
                    d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="#3070b6" stroke-width="2" />
                </svg>
              </div>
              <h3>{{ $t('viberCarousel.features.easy.title') }}</h3>
              <p>{{ $t('viberCarousel.features.easy.description') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- API Examples Section -->
    <div class="page-section bg-gray-50" id="api">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('viberCarousel.api.title') }}</div>
          <div class="section-head-description">{{ $t('viberCarousel.api.subtitle') }}</div>
        </div>
        <div class="section-content">
          <div class="api-examples">
            <div class="api-tabs">
              <button v-for="(example, index) in apiExamples" :key="index"
                :class="['api-tab', { active: activeExample === index }]" @click="activeExample = index">
                {{ example.title }}
              </button>
            </div>
            <div class="api-content">
              <div class="api-description">
                <h4>{{ apiExamples[activeExample].title }}</h4>
                <p>{{ apiExamples[activeExample].description }}</p>
              </div>
              <div class="code-block">
                <div class="code-header">
                  <span class="code-language">{{ apiExamples[activeExample].language }}</span>
                  <button class="copy-button" @click="copyToClipboard(apiExamples[activeExample].code)">
                    Copy
                  </button>
                </div>
                <pre><code>{{ apiExamples[activeExample].code }}</code></pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pricing Section -->
    <div class="page-section" id="pricing">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('viberCarousel.pricing.title') }}</div>
          <div class="section-head-description">{{ $t('viberCarousel.pricing.subtitle') }}</div>
        </div>
        <div class="section-content">
          <div class="pricing-table-container">
            <LazyViberCarouselRatesTable />
          </div>
        </div>
      </div>
    </div>

    <!-- CTA Section -->
    <div class="page-section bg-gradient bottom-banner">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('viberCarousel.cta.title') }}</div>
          <div class="section-head-description text-gray-600">{{ $t('viberCarousel.cta.description') }}</div>
        </div>
        <div class="section-content">
          <div class="banner-action flex flex-wrap items-center justify-center gap-4">
            <NuxtLink to="https://omni.smsbat.com/register-ca" external target="_blank" class="nav-link button filled">
              <p>{{ $t('viberCarousel.cta.getStarted') }}</p>
            </NuxtLink>
            <NuxtLink to="https://help.smsbat.com/uk/hc/1780048863/1/api?category_id=3#viber_carousel" external
              target="_blank" class="button outlined">
              <p>{{ $t('viberCarousel.cta.viewDocs') }}</p>
            </NuxtLink>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.bg-gradient {
  background: linear-gradient(135deg, #4f46e5 0%, #764ba2 100%);
  color: white;

  .section-head-title {
    color: white;
  }

  .text-gray-600 {
    color: rgba(255, 255, 255, 0.8);
  }

  .button.outlined {
    background: transparent;
  }
}

.viber-carousel-page {
  min-height: 100vh;
}

.main-banner {
  padding: 40px 0;
  min-height: 80vh;
  display: flex;
  align-items: center;
}

.banner-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
}

.banner-carousel-text h1 {
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 20px;
  line-height: 1.2;
}

.banner-description {
  font-size: 1.3rem;
  margin-bottom: 15px;
  opacity: 0.9;
}

.banner-description-secondary {
  font-size: 1.1rem;
  margin-bottom: 40px;
  opacity: 0.8;
}

.banner-buttons {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.btn {
  padding: 15px 30px;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  display: inline-block;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.btn-primary {
  background: white;
  color: #4f46e5;
  border-color: white;
}

.btn-primary:hover {
  background: #f8f9fa;
  transform: translateY(-2px);
}

.btn-outline {
  background: transparent;
  color: white;
  border-color: white;
}

.btn-outline:hover {
  background: white;
  color: #4f46e5;
}

.btn-large {
  padding: 18px 40px;
  font-size: 1.1rem;
}

.carousel-preview {
  display: flex;
  justify-content: center;
  align-items: center;
}

.carousel-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  max-width: 300px;
  color: #333;
}

.carousel-image {
  width: 100%;
  height: 200px;
  overflow: hidden;
}

.carousel-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.carousel-content {
  padding: 20px;
}

.carousel-content h3 {
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 15px;
  color: #2d3748;
}

.carousel-buttons {
  display: flex;
  gap: 10px;
}

.carousel-buttons button {
  flex: 1;
  padding: 10px 15px;
  border-radius: 6px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
}

.carousel-buttons .btn-primary {
  background: #3070b6;
  color: white;
}

.carousel-buttons .btn-secondary {
  background: #f8f9fa;
  color: #3070b6;
  border: 1px solid #e9ecef;
}

.page-section {
  padding: 80px 0;
}

.section-head {
  text-align: center;
  margin-bottom: 60px;
}

.section-head-title {
  font-size: 2.5rem;
  font-weight: 800;
  color: #2d3748;
  margin-bottom: 20px;
  line-height: 1.2;
}

.section-head-description {
  font-size: 1.2rem;
  max-width: 600px;
  margin: 0 auto;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 40px;
  max-width: 1200px;
  margin: 0 auto;
}

.feature-item {
  text-align: center;
  padding: 40px 30px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.feature-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 40px rgba(123, 81, 157, 0.15);
}

.feature-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(123, 81, 157, 0.1);
  border-radius: 20px;
}

.feature-item h3 {
  font-size: 1.4rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 15px;
}

.feature-item p {
  color: #718096;
  line-height: 1.6;
  font-size: 1rem;
}

.bg-gray-50 {
  background: #f8f9fa;
}

.api-examples {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  max-width: 1000px;
  margin: 0 auto;
}

.api-tabs {
  display: flex;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.api-tab {
  flex: 1;
  padding: 15px 20px;
  background: none;
  border: none;
  cursor: pointer;
  font-weight: 600;
  color: #6c757d;
  transition: all 0.3s ease;
}

.api-tab.active {
  background: white;
  color: #4f46e5;
  border-bottom: 2px solid #4f46e5;
}

.api-tab:hover:not(.active) {
  background: #e9ecef;
}

.api-content {
  padding: 30px;
}

.api-description {
  margin-bottom: 25px;
}

.api-description h4 {
  font-size: 1.3rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 10px;
}

.api-description p {
  color: #718096;
  line-height: 1.6;
}

.code-block {
  background: #f8f9fa;
  border-radius: 8px;
  overflow: hidden;
}

.code-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background: #e9ecef;
  border-bottom: 1px solid #dee2e6;
}

.code-language {
  font-weight: 600;
  color: #495057;
}

.copy-button {
  background: #4f46e5;
  color: white;
  border: none;
  padding: 5px 12px;
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.copy-button:hover {
  background: #5a67d8;
}

.code-block pre {
  margin: 0;
  padding: 20px;
  overflow-x: auto;
}

.code-block code {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #2d3748;
}

.pricing-table-container {
  overflow-x: auto;
  max-width: 100%;
}

.pricing-table {
  width: 100%;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border-collapse: collapse;
}

.pricing-table th {
  background: #f8f9fa;
  padding: 20px;
  text-align: left;
  font-weight: 700;
  color: #2d3748;
  border-bottom: 2px solid #e9ecef;
}

.pricing-table td {
  padding: 15px 20px;
  border-bottom: 1px solid #e9ecef;
  color: #4a5568;
}

.pricing-table tr:hover {
  background: #f8f9fa;
}

/* Responsive Design */
@media (max-width: 768px) {
  .bottom-banner {
    margin-bottom: -40px;
  }

  .banner-content {
    grid-template-columns: 1fr;
    gap: 40px;
    text-align: center;
  }

  .banner-carousel-text h1 {
    font-size: 2.2rem;
  }

  .banner-description {
    font-size: 1.1rem;
  }

  .banner-description-secondary {
    font-size: 1rem;
  }

  .banner-buttons {
    justify-content: center;
  }

  .features-grid {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  .feature-item {
    padding: 30px 20px;
  }

  .api-tabs {
    flex-direction: column;
  }

  .api-tab {
    border-bottom: 1px solid #e9ecef;
  }

  .api-tab.active {
    border-bottom: none;
  }

  .pricing-table th,
  .pricing-table td {
    padding: 10px 15px;
  }

  .cta-buttons {
    flex-direction: column;
    align-items: center;
  }
}

@media (max-width: 480px) {
  .main-banner {
    padding: 60px 0;
  }

  .banner-carousel-text h1 {
    font-size: 1.8rem;
  }

  .section-head-title {
    font-size: 2rem;
  }

  .feature-item {
    padding: 25px 15px;
  }

  .pricing-table th,
  .pricing-table td {
    padding: 8px 12px;
  }
}

/* Global Stats Section */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  margin-bottom: 3rem;
}

.stat-item {
  text-align: center;
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.stat-number {
  font-size: 2.5rem;
  font-weight: bold;
  color: #3070b6;
  margin-bottom: 0.5rem;
}

.stat-label {
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.4;
}

.top-countries h3 {
  color: #2c3e50;
  margin-bottom: 2rem;
  text-align: center;
  font-size: 1.5rem;
}

.countries-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}

.country-group h4 {
  color: #3070b6;
  margin-bottom: 1rem;
  font-size: 1.1rem;
}

.country-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.country-item {
  color: #6c757d;
  font-size: 0.9rem;
  padding: 0.5rem;
  background: white;
  border-radius: 6px;
  border-left: 3px solid #3070b6;
}

@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }

  .countries-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
}

.banner-action {
  display: flex;
  gap: 20px;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 30px;
}

.banner-action .button {
  min-width: fit-content;
  white-space: nowrap;
  padding: 0 24px;
}

@media (max-width: 768px) {
  .main-banner {
    .banner-title {
      margin: 0 auto;
      text-align: center;
    }

    .banner-description {
      margin: 0 auto;
      text-align: center;
    }
  }
}
</style>
